import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';

// Hover delay before a tooltip shows; keyboard focus uses it too (Tooltip, Figma 1163:4474).
export const TOOLTIP_DELAY_MS = 500;
const EDGE = 16;
const OFFSET = 8;

/**
 * One tooltip for every `[data-tip]` inside `scopeRef`: it shows after TOOLTIP_DELAY_MS on hover
 * or keyboard focus, with no fade, and hides at once. `data-tip` lines split on "\n";
 * `data-tip-note` adds " · note" at opacity-60 after the last line. The element is described by
 * the tooltip while it shows. It can't be hovered onto (a design-system decision).
 */
function TooltipLayer({ scopeRef }) {
  const id = useId();
  const tipRef = useRef(null);
  const [tip, setTip] = useState(null);

  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope) return undefined;
    let timer = null;
    let shownFor = null;
    let ownDescribedBy = null;

    // The element keeps any description of its own: the tooltip's id is added while it shows,
    // and the original value comes back on hide.
    const hide = () => {
      clearTimeout(timer);
      if (shownFor) {
        if (ownDescribedBy) shownFor.setAttribute('aria-describedby', ownDescribedBy);
        else shownFor.removeAttribute('aria-describedby');
      }
      shownFor = null;
      setTip(null);
    };
    const arm = (e) => {
      const el = e.target.closest?.('[data-tip]');
      if (!el || !scope.contains(el) || el === shownFor) return;
      if (el.getAttribute('aria-expanded') === 'true') return;
      hide();
      timer = setTimeout(() => {
        shownFor = el;
        ownDescribedBy = el.getAttribute('aria-describedby');
        el.setAttribute('aria-describedby', ownDescribedBy ? `${ownDescribedBy} ${id}` : id);
        setTip({ el, lines: el.dataset.tip.split('\n'), note: el.dataset.tipNote || null });
      }, TOOLTIP_DELAY_MS);
    };
    const onFocusIn = (e) => { if (e.target.matches?.(':focus-visible')) arm(e); };
    const onMouseOut = (e) => {
      const el = e.target.closest?.('[data-tip]');
      if (el && !el.contains(e.relatedTarget)) hide();
    };

    scope.addEventListener('mouseover', arm);
    scope.addEventListener('focusin', onFocusIn);
    scope.addEventListener('mouseout', onMouseOut);
    scope.addEventListener('focusout', hide);
    document.addEventListener('mousedown', hide);
    document.addEventListener('scroll', hide, true);
    return () => {
      hide();
      scope.removeEventListener('mouseover', arm);
      scope.removeEventListener('focusin', onFocusIn);
      scope.removeEventListener('mouseout', onMouseOut);
      scope.removeEventListener('focusout', hide);
      document.removeEventListener('mousedown', hide);
      document.removeEventListener('scroll', hide, true);
    };
  }, [scopeRef, id]);

  // Above the element, centred; below when there's no room; 16 inside the viewport.
  useLayoutEffect(() => {
    const node = tipRef.current;
    if (!tip || !node) return;
    const r = tip.el.getBoundingClientRect();
    const t = node.getBoundingClientRect();
    let top = r.top - t.height - OFFSET;
    if (top < EDGE) top = r.bottom + OFFSET;
    const left = Math.max(EDGE, Math.min(r.left + r.width / 2 - t.width / 2, window.innerWidth - t.width - EDGE));
    node.style.top = `${top}px`;
    node.style.left = `${left}px`;
  }, [tip]);

  if (!tip) return null;
  return createPortal(
    <div ref={tipRef} id={id} role="tooltip" className="resui-tooltip">
      {tip.lines.map((line, i) => (
        <span key={i} className="resui-tooltip-line">
          {line}
          {tip.note && i === tip.lines.length - 1 && <span className="resui-tooltip-note"> · {tip.note}</span>}
        </span>
      ))}
    </div>,
    document.body,
  );
}

export default TooltipLayer;
